import { Component, OnDestroy, OnInit } from '@angular/core';
import { IUserContactGroup } from '../../store/models/user-group.model';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { ContactsService } from '../../store/services/contacts.service';
import { SessionService } from '../../store/services/session.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { IUserSessionData } from '../../store/models/user.model';
import { IContactGroup } from '../../store/models/contact.model';
import { ISelectItem } from '../../store/models/select.model';

export const MANAGE_OPTION_RENAME = 'rename';
export const MANAGE_OPTION_RESET = 'reset';
export const MANAGE_OPTION_MERGE = 'merge';
export const MANAGE_OPTION_DOWNLOAD = 'download';

export const MANAGE_GROUP_OPTIONS = {
  [MANAGE_OPTION_RENAME]: 'Rename contact group',
  [MANAGE_OPTION_RESET]: 'Reset contact group',
  [MANAGE_OPTION_MERGE]: 'Merge contact group',
  [MANAGE_OPTION_DOWNLOAD]: 'Download address & phone details',
};

export enum EResetOption {
  WITHOUT_EMAILS,
  ALL,
}

export const FORMAT_CSV = 'csv';
export const FORMAT_XLS = 'xls';

@Component({
  selector: 'app-manage-contact-groups',
  templateUrl: './manage-contact-groups.component.html',
  styleUrls: ['./manage-contact-groups.component.scss'],
})
export class ManageContactGroupsComponent implements OnInit, OnDestroy {
  contactGroup: IUserContactGroup;
  inputForm: FormGroup;
  ready: boolean;
  contactGroups: IContactGroup[];

  private readonly destroy$: Subject<boolean> = new Subject<boolean>();
  private userSession: IUserSessionData;

  constructor(
    private readonly contactsService: ContactsService,
    private readonly sessionService: SessionService
  ) {}

  ngOnInit(): void {
    this.sessionService
      .onReady()
      .pipe(takeUntil(this.destroy$))
      .subscribe(session => {
        if (!session) {
          return;
        }
        this.userSession = session;
        this.contactsService
          .getContactGroups(this.userSession.id)
          .subscribe(result => {
            this.contactGroups = result;
            if (this.inputForm) {
              this.inputForm.addControl(
                'option',
                new FormControl(MANAGE_OPTION_RENAME, [Validators.required])
              );
              this.inputForm.addControl(
                'group_name',
                new FormControl(this.contactGroup.group_name, [
                  Validators.required,
                ])
              );
              this.inputForm.addControl(
                'reset_option',
                new FormControl(EResetOption.WITHOUT_EMAILS, [
                  Validators.required,
                ])
              );
              this.inputForm.addControl('group_id', new FormControl('', []));
              this.inputForm.addControl(
                'download_format',
                new FormControl(FORMAT_CSV, [Validators.required])
              );
              this.ready = true;
            }
          });
      });
  }

  get option(): AbstractControl {
    if (!this.inputForm) {
      return null;
    }
    return this.inputForm.get('option');
  }

  get options(): string[] {
    return Object.keys(MANAGE_GROUP_OPTIONS);
  }

  getOptionValue(key: string): string {
    return MANAGE_GROUP_OPTIONS[key];
  }

  get contactGroupsList(): ISelectItem[] {
    return this.contactGroups.map(c => ({
      label: c.group_name,
      value: c.group_id,
    }));
  }

  get formats(): ISelectItem[] {
    return [
      {
        label: 'CSV',
        value: FORMAT_CSV,
      },
      {
        label: 'XLS',
        value: FORMAT_XLS,
      },
    ];
  }

  ngOnDestroy() {
    this.destroy$.next(true);
  }

  optionsChange() {
    const value = this.option.value;

    switch (value) {
      case MANAGE_OPTION_MERGE:
        this.inputForm.get('group_id').setValidators([Validators.required]);
        break;
      default:
        this.inputForm.get('group_id').setValidators([]);
        break;
    }

    this.inputForm.get('group_id').updateValueAndValidity();
  }
}
